import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getAuthElemStyle, isEmail, isPassword, setFocus } from '../../../../common/tools';
import { ISuccessfulAuth } from '../../../../interfaces';
import { MainContext } from '../../../../context/main.context';
import { useNavigate } from 'react-router-dom';
import { ArrowBackSvg, CrossedOutEyeSvg, EyeSvg } from '../../../../icons';
import VerificationCode from '../verification-code';
import { REGISTER_BY_EMAIL_ERROR } from '../../../../common/errorCodes';
import Header from '../../../header';
import Recaptcha from '../../recaptcha';
import Footer from '../../../footer';
import ManualController from '../../../../hoc/ManualController';
import { registerByEmail } from '../../../../stores/auth.store';
import i18n from 'i18next';
import LoginByMail from '../login';


export default function Registration() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [ hidePassword, setHidePassword ] = useState<boolean>(true);
    const [ email , setEmail ] = useState<string>('');
    const [ password, setPassword ] = useState<string>('');
    const [ showVerificationCodeComponent, setShowVerificationCodeComponent ] = useState<boolean>(false);
    const { manualRedirect } = ManualController();
    const { language } = i18n;
    const [ isDisableBtn, setDisableBtn ] = useState<boolean>(true);
    const [ emailErr , setEmailErr ] = useState<string>('');
    const [ passwordErr, setPasswordErr ] = useState<string>('');
    const {
        showCaptcha,
        session,
        locale,
        project,
        source,
        settings,
        toggleLoader,
        modalContent,
        setModalContent,
        failedAttemptCenter,
    } = useContext(MainContext);

    useEffect(() => {
        if (email && password && !showCaptcha) {
            setDisableBtn(false);
        } else {
            setDisableBtn(true);
        }
    }, [ email, password, showCaptcha ]);

    const _onSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
        e.preventDefault();

        if (!email) {
            setFocus('req-email-id');
            setEmailErr(t('login.enterEmail'));
            return;
        }

        if (!isEmail(email)) {
            setFocus('req-email-id');
            setEmailErr(t('login.invalidEmail'));
            return;
        }

        if (emailErr) {
            setEmailErr('');
        }

        if (!password) {
            setFocus('req-password-id');
            setPasswordErr(t('login.enterPassword'));
            return;
        }

        if (!isPassword(password)) {
            setFocus('req-password-id');
            setPasswordErr(t('login.passwordValidation'));
            return;
        }

        if (passwordErr) {
            setPasswordErr('');
        }

        _registerByEmail();
    }

    const _failedAttemptCenter = (): void => {
        if (failedAttemptCenter) {
            failedAttemptCenter('add');
        }
    }

    const _registerByEmail = (): void => {
        toggleLoader(true);

        registerByEmail(email, password).then((res: { data: ISuccessfulAuth }): void => {
            const warningMessage: string|undefined = res?.data?.warningMessage;

            if (warningMessage === 'USER_EXIST_REGISTER_BY_EMAIL') {
                setEmailErr(t('login.userExists'));
                setPassword('');
                _failedAttemptCenter();
                return;
            }

            const result: string|undefined = res?.data?.result;

            if (result) {
                setShowVerificationCodeComponent(true);
                return;
            }

            manualRedirect(res, 'auth', true, REGISTER_BY_EMAIL_ERROR);
        }, err => {
            const errData = err?.response?.data || err;

            manualRedirect(errData, 'auth', true, REGISTER_BY_EMAIL_ERROR);
        }).finally((): void => {
            toggleLoader(false);
        });
    }

    const _navigate = (path: string): void => {
        if (session) {
            navigate({
                pathname: path,
                search: `?session=${session}&source=${source}&locale=${locale}&project=${project}`,
            });
        } else {
            navigate(path);
        }
    }

    const _onClose = (): void => {
        setPassword('');
        setShowVerificationCodeComponent(false);
    }

    const _renderMenu = () => {
        return (
            <div className="auth-modal-menu">
                <div className="login-modal-header">
                    <div
                        className="login-modal-header-prev"
                        onClick={() => {
                            if (modalContent) {
                                setModalContent({ component: <LoginByMail/> });
                                return;
                            }

                            _navigate('/session/login/email');
                        }}
                    >
                        <ArrowBackSvg/>
                        {t('common.back')}
                    </div>
                    <div className="login-modal-header-title text-overflow">
                        {t('login.registration')}
                    </div>
                </div>
                <form
                    noValidate
                    className="auth-modal-menu-body req-auth-modal-menu-body"
                    onSubmit={(e) => _onSubmit(e)}
                >
                    <div className="login-modal-menu-body-input">
                        <div className="login-modal-menu-body-input-title">
                            {t('login.emailLabel')}
                        </div>
                        <input
                            className="login-modal-menu-body-input-item"
                            type="email"
                            id="req-email-id"
                            autoComplete="off"
                            autoFocus
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            style={{border: `1px solid ${emailErr ? '#B01E1E' : '#d1d1d0'}`}}
                        >
                        </input>
                        { emailErr ? <div className="req-input-err">{emailErr}</div> : null }
                    </div>
                    <div className="login-modal-menu-body-input">
                        <div className="login-modal-menu-body-input-title">
                            {t('login.passwordLabel')}
                        </div>
                        <div className="login-modal-menu-body-input-password">
                            <input
                                className="login-modal-menu-body-input-item login-password"
                                type={hidePassword ? 'password' : 'text'}
                                id="req-password-id"
                                autoComplete="off"
                                title={t('login.passwordValidation')}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                style={{border: `1px solid ${passwordErr ? '#B01E1E' : '#d1d1d0'}`}}
                            >
                            </input>
                            <div className="password-hide" onClick={() => setHidePassword(!hidePassword)}>
                                {hidePassword ? <EyeSvg/> : <CrossedOutEyeSvg/>}
                            </div>
                            { passwordErr ? <div className="req-input-err">{passwordErr}</div> : null }
                        </div>
                    </div>
                    <div className="reg-agreement">
                        <div className="reg-agreement-title">
                            <span>{t('login.agreement')}</span>
                            &nbsp;
                            <span
                                className="login-personal-data"
                                onClick={() => {
                                    const fileName: string = language === 'ru' ? 'RuPersonalData' : 'EnPersonalData';
                                    window.location.href = `${window.location.origin}/docs/${fileName}.html`;
                                }}
                            >
                                {t('login.agreementLink')}
                            </span>
                        </div>
                    </div>
                    <button
                        className="btn"
                        style={{
                            background: isDisableBtn ? '#D1D1D0' : '#2368a2',
                            cursor: isDisableBtn ? 'auto' : 'pointer',
                        }}
                        type="submit"
                        disabled={isDisableBtn}
                    >
                        {t('login.submit')}
                    </button>
                    <div className="auth-modal-menu-footer req-auth-modal-menu-footer">
                        <div className="auth-modal-menu-footer-no-acc text-overflow">
                            {t('login.accExist')}?
                        </div>
                        <div
                            className="auth-modal-menu-footer-reg text-overflow"
                            onClick={() => {
                                if (modalContent) {
                                    setModalContent({ component: <LoginByMail/> });
                                    return;
                                }

                                _navigate('/session/login/email');
                            }}
                        >
                            {t('login.loginButton')}
                        </div>
                    </div>
                </form>
                <Recaptcha/>
            </div>
        )
    }

    return (
        <div className="auth" id="auth-element-id" style={{background: !modalContent ? '#e8e8e8' : 'unset'}}>
            <div
                className="auth-container"
                style={{
                    background: !modalContent ? '#e8e8e8' : 'unset',
                    minHeight: !modalContent ? 'calc(100vh - 100px)' : '100%',
                }}
            >
                <div className="auth-modal" style={getAuthElemStyle(settings)}>
                    <Header origin="auth"/>
                    {
                        !showVerificationCodeComponent ?
                            _renderMenu()
                        :
                            <VerificationCode
                                type="registration"
                                email={email}
                                onClose={() => _onClose()}
                                onResend={() => _registerByEmail()}
                            />
                    }
                </div>
            </div>
            {!modalContent && <Footer/>}
        </div>
    );
}